<template>
  <div class="download-page">
    <!-- 页面标题 -->
    <div class="page-title">大气校正软件下载</div>

    <!-- 加载状态 -->
    <div v-if="isLoading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>正在加载软件版本信息...</p>
    </div>

    <!-- 错误提示 -->
    <div v-if="error" class="error-message">
      <p>{{ error }}</p>
      <button @click="fetchAllVersions" class="retry-btn">重试</button>
    </div>

    <!-- 所有软件版本信息 -->
    <div v-if="!isLoading && !error" class="software-sections">
      <div v-for="software in softwareList" :key="software.id" class="software-section">
        <h2 class="software-title">{{ software.name }}</h2>
        
        <!-- 主要下载区域 -->
        <div class="main-download-area">
          <!-- 最新版本展示卡片 -->
          <div class="latest-version-showcase">
            <div class="showcase-background">
              <div class="glow-effect"></div>
              <div class="particle-effect"></div>
            </div>
            
            <div class="showcase-content">
              <div class="software-header">
                <div class="software-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M2 17L12 22L22 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M2 12L12 17L22 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <div class="software-title-group">
                  <h3 class="software-name">{{ software.name }}</h3>
                  <div class="software-subtitle">遥感数据处理专业版</div>
                </div>
                <div class="latest-badge">
                  <!-- <span class="badge-icon">⭐</span> -->
                  <span class="badge-text">最新版本</span>
                </div>
              </div>

              <div v-if="getLatestVersion(software.id)" class="version-showcase-body">
                <div class="version-main-info">
                  <div class="version-number-display">
                    <span class="version-label">版本号</span>
                    <span class="version-number-large">{{ getLatestVersion(software.id).version_number }}</span>
                  </div>
                  <div class="version-date-display">
                    <span class="date-icon">📅</span>
                    <span class="date-text">{{ formatDate(getLatestVersion(software.id).release_date) }}</span>
                  </div>
                </div>

                <div class="version-description-box">
                  <div class="description-header">
                    <span class="description-icon">📝</span>
                    <span class="description-title">版本说明</span>
                  </div>
                  <div class="description-content">
                  <p v-if="getLatestVersion(software.id).update_log">{{ getLatestVersion(software.id).update_log }}</p>
                    <p v-else class="default-description">
                      <span class="highlight-text">✨ 最新稳定版本</span><br>
                      经过全面测试，性能稳定可靠，推荐使用
                    </p>
                </div>
                </div>

                <div class="download-action-area">
                  <button class="download-btn-enhanced" @click="handleDownload(getLatestVersion(software.id))">
                    <span class="btn-glow"></span>
                    <span class="btn-content">
                      <span class="download-icon-animated">⬇</span>
                      <span class="btn-text">立即下载</span>
                    </span>
                    <span class="btn-shine"></span>
                </button>
                  <div class="download-tips">
                    <span class="tip-icon">💡</span>
                    <span class="tip-text">免费下载 · 安全可靠 · 专业支持</span>
              </div>
                </div>
              </div>
              <div v-else class="no-version-showcase">
                <div class="no-version-icon">📦</div>
                <p class="no-version-text">暂无版本信息</p>
                <p class="no-version-hint">请稍后查看或联系管理员</p>
              </div>
            </div>
          </div>

          <!-- 更多版本按钮 -->
          <div class="more-versions-section">
            <button 
              class="more-versions-btn" 
              @click="toggleMoreVersions(software.id)"
              v-if="getVersionsBySoftware(software.id).length > 1"
            >
              <span class="btn-text">
                {{ showMoreVersions[software.id] ? '收起其他版本' : `查看其他版本 (${getVersionsBySoftware(software.id).length - 1})` }}
              </span>
              <span class="btn-icon" :class="{ 'rotated': showMoreVersions[software.id] }">▼</span>
            </button>
          </div>

          <!-- 展开的历史版本和更新日志 -->
          <div v-if="showMoreVersions[software.id]" class="expanded-versions">
            <div class="expanded-cards">
          <!-- 历史版本下载卡片 -->
          <div class="history-card">
            <div class="card-header">
              <h3>历史版本下载</h3>
                  <div class="version-count">{{ getVersionsBySoftware(software.id).length - 1 }} 个版本</div>
            </div>
            <div class="card-content">
                  <div v-if="getVersionsBySoftware(software.id).length > 1" class="version-list">
                <div v-for="ver in getDisplayedVersions(software.id)" :key="ver.id" class="version-item">
                  <div class="version-details">
                    <span class="version-number">{{ ver.version_number }}</span>
                    <span class="release-date">{{ formatDate(ver.release_date) }}</span>
                  </div>
                  <button class="download-btn-small" @click="handleDownload(ver)">
                    下载
                  </button>
                </div>
                    <button v-if="getVersionsBySoftware(software.id).length > 4" @click="toggleHistory(software.id)" class="more-btn">
                      {{ showAllHistory[software.id] ? '收起' : `查看更多 (${getVersionsBySoftware(software.id).length - 4})` }}
                </button>
              </div>
              <div v-else class="no-version">
                <p>暂无历史版本</p>
              </div>
            </div>
          </div>

          <!-- 更新日志卡片 -->
          <div class="changelog-card">
            <div class="card-header">
              <h3>更新日志</h3>
              <div class="log-count">{{ getVersionsBySoftware(software.id).length }} 条记录</div>
            </div>
            <div class="card-content">
              <div v-if="getVersionsBySoftware(software.id).length > 0" class="changelog-list">
                <div v-for="log in getDisplayedLogs(software.id)" :key="log.id" class="changelog-item">
                  <div class="version-header">
                    <span class="version-number">{{ log.version_number }}</span>
                    <span class="release-date">{{ formatDate(log.release_date) }}</span>
                  </div>
                  <div class="update-content">
                    <p v-if="log.update_log">{{ log.update_log }}</p>
                    <p v-else class="no-log">暂无更新说明</p>
                  </div>
                </div>
                <button v-if="getVersionsBySoftware(software.id).length > 3" @click="toggleLogs(software.id)" class="more-btn">
                  {{ showAllLogs[software.id] ? '收起' : `查看更多 (${getVersionsBySoftware(software.id).length - 3})` }}
                </button>
              </div>
              <div v-else class="no-version">
                <p>暂无更新日志</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 二维码弹窗 -->
    <div v-if="showQRModal" class="qr-modal-overlay" @click="closeQRModal">
      <div class="qr-modal" @click.stop>
        <div class="qr-modal-header">
          <h3>扫码下载</h3>
          <button class="close-btn" @click="closeQRModal">×</button>
        </div>
        <div class="qr-modal-content">
          <div class="qr-code-container">
            <div v-if="selectedVersion && selectedVersion.file_type === 'upload'" class="qr-code-image">
              <img 
                :src="`https://abc.atmcorr.com/api/uploads//versions/${selectedVersion.file_path}`" 
                :alt="`下载 ${selectedVersion.version_number}`"
                @error="handleImageError"
                @load="handleImageLoad"
                class="download-image"
              />
              <div v-if="imageLoading" class="image-loading">
                <div class="loading-spinner"></div>
                <p>图片加载中...</p>
              </div>
            </div>
            <div v-else class="qr-code-placeholder">
              <div class="qr-icon">📱</div>
              <p>二维码加载中...</p>
            </div>
          </div>
          <div class="download-tips">
            <h4>下载提示：</h4>
            <ul>
              <li>扫描二维码进行下载</li>
              <li>下载完成后请按照安装提示进行安装</li>
            </ul>
          </div>
        </div>
        <div class="qr-modal-footer">
          <button class="cancel-btn" @click="closeQRModal">取消</button>
          
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { ElMessage } from 'element-plus';
import api from '@/utils/axios';

export default {
  name: 'Download',
  data() {
    return {
      softwareList: [
        { id: 1, name: 'GAC_MSS' },
        { id: 2, name: 'GAC_PMS' },
        { id: 3, name: 'GAC_CCD' },
        { id: 4, name: 'GAC_AHSI_Water' },
        { id: 5, name: 'GAC_AHSI_Mineral' }
      ],
      allSoftwareVersions: {}, // 存储所有软件的版本数据
      showAllHistory: {}, // 每个软件的历史版本展开状态
      showAllLogs: {}, // 每个软件的日志展开状态
      showMoreVersions: {}, // 每个软件的更多版本展开状态
      showQRModal: false,
      selectedVersion: null,
      imageLoading: false,
      isLoading: false,
      error: null
    };
  },
  computed: {
    ...mapState({
      isLoggedIn: state => state.user.isAuthenticated
    }),
    
    // 获取指定软件的最新版本
    getLatestVersion() {
      return (softwareId) => {
        const versions = this.allSoftwareVersions[softwareId] || [];
        return versions.length > 0 ? versions[0] : null;
      };
    },
    
    // 获取指定软件的所有版本
    getVersionsBySoftware() {
      return (softwareId) => {
        return this.allSoftwareVersions[softwareId] || [];
      };
    },
    
    // 获取指定软件的显示版本（历史版本）
    getDisplayedVersions() {
      return (softwareId) => {
        const versions = this.getVersionsBySoftware(softwareId);
        return this.showAllHistory[softwareId] 
          ? versions.slice(1) 
          : versions.slice(1, 4);
      };
    },
    
    // 获取指定软件的显示日志
    getDisplayedLogs() {
      return (softwareId) => {
        const versions = this.getVersionsBySoftware(softwareId);
        return this.showAllLogs[softwareId] 
          ? versions 
          : versions.slice(0, 3);
      };
    }
  },
  methods: {
    // 切换更多版本显示
    toggleMoreVersions(softwareId) {
      this.showMoreVersions = { ...this.showMoreVersions, [softwareId]: !this.showMoreVersions[softwareId] };
    },
    
    // 切换历史版本显示
    toggleHistory(softwareId) {
      this.showAllHistory = { ...this.showAllHistory, [softwareId]: !this.showAllHistory[softwareId] };
    },
    
    // 切换日志显示
    toggleLogs(softwareId) {
      this.showAllLogs = { ...this.showAllLogs, [softwareId]: !this.showAllLogs[softwareId] };
    },
    
    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
    },
    
    // 获取所有软件的版本列表
    async fetchAllVersions() {
      this.isLoading = true;
      this.error = null;
      
      try {
        console.log('开始获取所有软件版本...');
        
        // 为每个软件获取版本数据
        const promises = this.softwareList.map(async (software) => {
          try {
            console.log(`正在获取 ${software.name} 的版本信息...`);
            const response = await api.get(`/downloads/versions?software_name=${software.name}`);
            console.log(`${software.name} 响应:`, response);
            
            const versions = Array.isArray(response.data) ? response.data : 
                           Array.isArray(response) ? response : [];
            
            console.log(`${software.name} 版本数据:`, versions);
            
            // 按发布日期排序
            const sortedVersions = versions.sort((a, b) => 
              new Date(b.release_date) - new Date(a.release_date)
            );
            
            this.allSoftwareVersions = { ...this.allSoftwareVersions, [software.id]: sortedVersions };
            console.log(`${software.name} 版本数量:`, sortedVersions.length);
          } catch (error) {
            console.error(`获取${software.name}版本失败:`, error);
            console.error('错误详情:', error.response);
            this.allSoftwareVersions = { ...this.allSoftwareVersions, [software.id]: [] };
          }
        });
        
        await Promise.all(promises);
        console.log('所有软件版本获取完成:', this.allSoftwareVersions);
        
      } catch (error) {
        console.error('获取软件版本失败:', error);
        console.error('错误详情:', error.response);
        this.error = error.response?.data?.message || error.message || '获取软件版本失败';
        ElMessage.error(this.error);
      } finally {
        this.isLoading = false;
      }
    },

    // 处理下载 - 显示二维码弹窗
    async handleDownload(version) {
      if (!this.isLoggedIn) {
        ElMessage.warning('请先登录后再下载');
        this.$router.push('/login');
        return;
      }

      try {
        // 先记录下载信息
        await api.post('/downloads/record', {
          version_id: version.id
        });
        console.log('下载记录成功');
        
        // 判断文件类型
        if (version.file_type === 'upload') {
          // 如果是上传的文件，判断是否为图片
          const filePath = version.file_path;
          const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
          const isImage = imageExtensions.some(ext => 
            filePath.toLowerCase().endsWith(ext)
          );
          
          if (isImage) {
            // 如果是图片，显示弹窗
            this.selectedVersion = version;
            this.showQRModal = true;
            this.imageLoading = true;
          } else {
            // 如果是软件包，直接下载
            window.location.href = `https://abc.atmcorr.com/api/uploads/versions/${version.file_path}`;
            ElMessage.success('开始下载...');
          }
        } else {
          // 如果是外部链接，直接下载
          window.location.href = version.file_path;
          ElMessage.success('开始下载...');
        }
      } catch (error) {
        console.error('记录下载信息失败:', error);
        ElMessage.error('下载记录失败，请稍后重试');
      }
    },

    // 关闭二维码弹窗
    closeQRModal() {
      this.showQRModal = false;
      this.selectedVersion = null;
      this.imageLoading = false;
    },

    // 确认下载
    async confirmDownload() {
      if (!this.selectedVersion) return;

      try {
        // 根据文件类型构建下载链接
        if (this.selectedVersion.file_type === 'upload') {
          // 如果是上传的文件，直接使用文件路径下载
          window.location.href = `https://abc.atmcorr.com/api/uploads/versions/${this.selectedVersion.file_path}`;
        } else {
          // 如果是外部链接，直接跳转
          window.location.href = this.selectedVersion.file_path;
        }

        // 关闭弹窗
        this.closeQRModal();
        ElMessage.success('开始下载...');
      } catch (error) {
        console.error('下载失败:', error);
        ElMessage.error('下载失败');
      }
    },

    // 处理图片加载错误
    handleImageError() {
      console.error('图片加载失败');
      this.imageLoading = false;
      ElMessage.error('图片加载失败，请稍后重试');
    },

    // 处理图片加载完成
    handleImageLoad() {
      this.imageLoading = false;
    }
  },
  created() {
    // 组件创建时获取所有软件版本列表
    this.fetchAllVersions();
  }
};
</script>

<style scoped>
.download-page {
  background: var(--bg-dark);
  padding: 40px 20px;
  font-family: 'Orbitron', sans-serif;
  color: var(--text-primary);
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  overflow: hidden;
}

.download-page::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: 
    radial-gradient(circle at 50% 50%, rgba(0, 180, 255, 0.1) 0%, transparent 50%),
    linear-gradient(45deg, rgba(0, 247, 255, 0.05) 25%, transparent 25%),
    linear-gradient(-45deg, rgba(255, 0, 255, 0.05) 25%, transparent 25%);
  background-size: 100% 100%, 60px 60px, 60px 60px;
  background-position: center center, 0 0, 0 0;
  pointer-events: none;
  z-index: 0;
}

.page-title {
  font-size: 28px;
  font-weight: bold;
  color: var(--primary);
  margin-bottom: 30px;
  text-align: center;
  text-shadow: 0 0 10px rgba(0, 180, 255, 0.5);
}

/* 软件区域容器 */
.software-sections {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

.software-section {
  background: var(--card-bg);
  border-radius: 16px;
  padding-right: 32px;
  padding-left: 32px;
  box-shadow: 0 0 20px rgba(0, 180, 255, 0.1);
  border: 1px solid rgba(0, 180, 255, 0.2);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.software-section:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(0, 180, 255, 0.15);
  border-color: rgba(0, 180, 255, 0.3);
}

.software-title {
  color: var(--primary);
  font-size: 2rem;
  margin-bottom: 24px;
  text-shadow: 0 0 15px rgba(0, 180, 255, 0.5);
  text-align: center;
  position: relative;
}

.software-title::after {
  content: '';
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 3px;
  background: var(--quantum-glow);
  border-radius: 2px;
}

/* 主要下载区域 */
.main-download-area {
  margin-top: 20px;
}

/* 最新版本展示卡片 */
.latest-version-showcase {
  background: linear-gradient(135deg, rgba(10, 10, 25, 0.95), rgba(5, 5, 15, 0.98));
  border-radius: 12px;
  box-shadow: 
    0 4px 16px rgba(0, 180, 255, 0.2),
    0 0 40px rgba(0, 180, 255, 0.1),
    inset 0 1px 1px rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(0, 180, 255, 0.3);
  backdrop-filter: blur(20px);
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  max-width: 500px;
  margin: 0 auto;
}

.latest-version-showcase:hover {
  transform: translateY(-4px);
  box-shadow: 
    0 12px 48px rgba(0, 180, 255, 0.3),
    0 0 100px rgba(0, 180, 255, 0.15),
    inset 0 1px 1px rgba(255, 255, 255, 0.15);
  border-color: rgba(0, 180, 255, 0.5);
}

/* 背景效果 */
.showcase-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  pointer-events: none;
}

.glow-effect {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(0, 180, 255, 0.15) 0%, transparent 70%);
  animation: glowPulse 4s ease-in-out infinite;
}

@keyframes glowPulse {
  0%, 100% { opacity: 0.5; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.1); }
}

.particle-effect {
  position: absolute;
  width: 100%;
  height: 100%;
  background-image: 
    radial-gradient(2px 2px at 20% 30%, rgba(0, 180, 255, 0.3), transparent),
    radial-gradient(2px 2px at 60% 70%, rgba(0, 247, 255, 0.3), transparent),
    radial-gradient(1px 1px at 50% 50%, rgba(255, 0, 255, 0.2), transparent),
    radial-gradient(2px 2px at 80% 10%, rgba(0, 180, 255, 0.3), transparent);
  background-size: 200% 200%;
  animation: particleFloat 15s ease-in-out infinite;
}

@keyframes particleFloat {
  0%, 100% { background-position: 0% 0%; }
  50% { background-position: 100% 100%; }
}

/* 展示内容 */
.showcase-content {
  position: relative;
  z-index: 1;
  padding: 18px;
}

/* 软件头部 */
.software-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(0, 180, 255, 0.2);
}

.software-icon {
  width: 40px;
  height: 40px;
  background: var(--quantum-glow);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(0, 180, 255, 0.4);
  flex-shrink: 0;
}

.software-icon svg {
  width: 24px;
  height: 24px;
  color: var(--text-primary);
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

.software-title-group {
  flex: 1;
}

.software-name {
  color: var(--primary);
  font-size: 1.2rem;
  margin: 0 0 3px 0;
  text-shadow: 0 0 20px rgba(0, 180, 255, 0.6);
  font-weight: 700;
  letter-spacing: 0.5px;
}

.software-subtitle {
  color: var(--text-secondary);
  font-size: 0.75rem;
  opacity: 0.8;
}

.latest-badge {
  background: linear-gradient(135deg, rgba(0, 180, 255, 0.2), rgba(0, 247, 255, 0.15));
  border: 1px solid rgba(0, 180, 255, 0.4);
  padding: 6px 12px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  gap: 4px;
  box-shadow: 0 2px 10px rgba(0, 180, 255, 0.3);
}

.badge-icon {
  font-size: 0.9rem;
  animation: twinkle 2s ease-in-out infinite;
}

@keyframes twinkle {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.2); }
}

.badge-text {
  color: var(--text-primary);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* 版本展示主体 */
.version-showcase-body {
  display: grid;
  gap: 12px;
}

/* 版本主要信息 */
.version-main-info {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.version-number-display,
.version-date-display {
  background: rgba(0, 180, 255, 0.08);
  border: 1px solid rgba(0, 180, 255, 0.2);
  border-radius: 8px;
  padding: 12px;
  transition: all 0.3s ease;
}

.version-number-display:hover,
.version-date-display:hover {
  background: rgba(0, 180, 255, 0.12);
  border-color: rgba(0, 180, 255, 0.4);
  transform: translateY(-2px);
}

.version-label {
  display: block;
  color: var(--text-secondary);
  font-size: 0.7rem;
  margin-bottom: 4px;
  opacity: 0.8;
}

.version-number-large {
  display: block;
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--primary);
  text-shadow: 0 0 20px rgba(0, 180, 255, 0.6);
  letter-spacing: 0.5px;
}

.version-date-display {
  display: flex;
  align-items: center;
  gap: 8px;
}

.date-icon {
  font-size: 1.1rem;
}

.date-text {
  color: var(--text-primary);
  font-size: 0.9rem;
  font-weight: 500;
}

/* 版本描述框 */
.version-description-box {
  background: rgba(0, 180, 255, 0.05);
  border: 1px solid rgba(0, 180, 255, 0.15);
  border-radius: 8px;
  padding: 12px;
  transition: all 0.3s ease;
}

.version-description-box:hover {
  background: rgba(0, 180, 255, 0.08);
  border-color: rgba(0, 180, 255, 0.25);
}

.description-header {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 8px;
}

.description-icon {
  font-size: 1rem;
}

.description-title {
  color: var(--primary);
  font-size: 0.85rem;
  font-weight: 600;
}

.description-content {
  color: var(--text-primary);
  line-height: 1.5;
  font-size: 0.85rem;
}

.default-description {
  margin: 0;
}

.highlight-text {
  color: var(--primary);
  font-weight: 600;
  font-size: 0.95rem;
}

/* 下载操作区域 */
.download-action-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: 4px;
}

.download-btn-enhanced {
  position: relative;
  background: var(--quantum-glow);
  color: var(--text-primary);
  border: none;
  padding: 12px 32px;
  font-size: 0.95rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 700;
  box-shadow: 
    0 4px 20px rgba(0, 180, 255, 0.5),
    0 0 40px rgba(0, 180, 255, 0.3);
  overflow: hidden;
  min-width: 180px;
}

.download-btn-enhanced:hover {
  transform: translateY(-3px) scale(1.02);
  box-shadow: 
    0 8px 30px rgba(0, 180, 255, 0.6),
    0 0 60px rgba(0, 180, 255, 0.4);
}

.btn-glow {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  animation: btnGlow 3s linear infinite;
}

@keyframes btnGlow {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

.btn-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 10px;
}

.download-icon-animated {
  font-size: 1.1rem;
  animation: bounceDown 2s ease-in-out infinite;
}

@keyframes bounceDown {
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-3px); }
  60% { transform: translateY(-1px); }
}

.btn-shine {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  transform: rotate(45deg);
  animation: btnShine 3s ease-in-out infinite;
}

@keyframes btnShine {
  0% { transform: translateX(-100%) rotate(45deg); }
  100% { transform: translateX(100%) rotate(45deg); }
}

.download-tips {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-secondary);
  font-size: 0.8rem;
}

.tip-icon {
  font-size: 1rem;
}

.tip-text {
  opacity: 0.9;
}

/* 无版本信息展示 */
.no-version-showcase {
  text-align: center;
  padding: 30px 15px;
}

.no-version-icon {
  font-size: 2.5rem;
  margin-bottom: 10px;
  opacity: 0.5;
}

.no-version-text {
  color: var(--text-primary);
  font-size: 1rem;
  margin-bottom: 5px;
}

.no-version-hint {
  color: var(--text-secondary);
  font-size: 0.8rem;
  opacity: 0.7;
}

/* 更多版本按钮 */
.more-versions-section {
  display: flex;
  justify-content: center;
  margin: 24px 0;
}

.more-versions-btn {
  background: transparent;
  color: var(--primary);
  border: 2px solid rgba(0, 180, 255, 0.3);
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  font-weight: 500;
}

.more-versions-btn:hover {
  background: rgba(0, 180, 255, 0.1);
  border-color: var(--primary);
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 180, 255, 0.2);
}

.btn-icon {
  transition: transform 0.3s ease;
  font-size: 0.8rem;
}

.btn-icon.rotated {
  transform: rotate(180deg);
}

/* 展开的版本区域 */
.expanded-versions {
  margin-top: 24px;
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.expanded-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 20px;
}

/* 通用卡片样式 */
.history-card,
.changelog-card {
  background: rgba(10, 10, 15, 0.8);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 180, 255, 0.1);
  border: 1px solid rgba(0, 180, 255, 0.2);
  backdrop-filter: blur(10px);
  overflow: hidden;
  transition: all 0.3s ease;
  height: fit-content;
}

.history-card:hover,
.changelog-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0, 180, 255, 0.2);
  border-color: rgba(0, 180, 255, 0.4);
}

/* 卡片头部 */
.card-header {
  background: linear-gradient(135deg, rgba(0, 180, 255, 0.08), rgba(0, 247, 255, 0.03));
  padding: 16px 20px;
  border-bottom: 1px solid rgba(0, 180, 255, 0.15);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h3 {
  color: var(--primary);
  margin: 0;
  font-size: 1.1rem;
  text-shadow: 0 0 8px rgba(0, 180, 255, 0.4);
  font-weight: 600;
}

.version-badge,
.version-count,
.log-count {
  background: rgba(0, 180, 255, 0.15);
  color: var(--text-primary);
  padding: 3px 10px;
  border-radius: 15px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid rgba(0, 180, 255, 0.25);
}

/* 卡片内容 */
.card-content {
  padding: 20px;
}

/* 当前版本卡片特殊样式 */
.version-info {
  text-align: center;
}

.version-number {
  font-size: 1.6rem;
  font-weight: bold;
  color: var(--primary);
  margin-bottom: 6px;
  text-shadow: 0 0 12px rgba(0, 180, 255, 0.4);
}

.release-date {
  color: var(--text-secondary);
  font-size: 0.85rem;
  margin-bottom: 12px;
}

.version-description {
  margin-bottom: 18px;
  min-height: 35px;
}

.version-description p {
  color: var(--text-primary);
  line-height: 1.4;
  font-size: 0.9rem;
}

.download-btn-primary {
  background: var(--quantum-glow);
  color: var(--text-primary);
  border: none;
  padding: 12px 24px;
  font-size: 0.9rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  width: 100%;
}

.download-btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 180, 255, 0.3);
}

/* 历史版本列表 */
.version-list {
  max-height: 300px;
  overflow-y: auto;
}
/* 主题滚动条（历史版本列表） */
.version-list {
  scrollbar-width: thin; /* Firefox */
  scrollbar-color: var(--primary) transparent; /* Firefox */
}
.version-list::-webkit-scrollbar {
  width: 8px;
}
.version-list::-webkit-scrollbar-track {
  background: rgba(0, 180, 255, 0.06);
  border-radius: 8px;
}
.version-list::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, rgba(0, 180, 255, 0.6), rgba(0, 247, 255, 0.4));
  border-radius: 8px;
  border: 1px solid rgba(0, 180, 255, 0.35);
  box-shadow: inset 0 0 6px rgba(0, 180, 255, 0.3);
}
.version-list:hover::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, rgba(0, 180, 255, 0.8), rgba(0, 247, 255, 0.6));
  border-color: rgba(0, 180, 255, 0.5);
}

.version-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid rgba(0, 180, 255, 0.08);
}

.version-item:last-child {
  border-bottom: none;
}

.version-details {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.version-details .version-number {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--primary);
}

.version-details .release-date {
  font-size: 0.75rem;
  color: var(--text-secondary);
}

.download-btn-small {
  background: var(--quantum-glow);
  color: var(--text-primary);
  border: none;
  padding: 6px 12px;
  font-size: 0.8rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 500;
}

.download-btn-small:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 180, 255, 0.25);
}

/* 更新日志列表 */
.changelog-list {
  max-height: 300px;
  overflow-y: auto;
}
/* 主题滚动条（更新日志列表） */
.changelog-list {
  scrollbar-width: thin; /* Firefox */
  scrollbar-color: var(--primary) transparent; /* Firefox */
}
.changelog-list::-webkit-scrollbar {
  width: 8px;
}
.changelog-list::-webkit-scrollbar-track {
  background: rgba(0, 180, 255, 0.06);
  border-radius: 8px;
}
.changelog-list::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, rgba(0, 180, 255, 0.6), rgba(0, 247, 255, 0.4));
  border-radius: 8px;
  border: 1px solid rgba(0, 180, 255, 0.35);
  box-shadow: inset 0 0 6px rgba(0, 180, 255, 0.3);
}
.changelog-list:hover::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, rgba(0, 180, 255, 0.8), rgba(0, 247, 255, 0.6));
  border-color: rgba(0, 180, 255, 0.5);
}

.changelog-item {
  padding: 12px 0;
  border-bottom: 1px solid rgba(0, 180, 255, 0.08);
}

.changelog-item:last-child {
  border-bottom: none;
}

.version-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.version-header .version-number {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--primary);
}

.version-header .release-date {
  font-size: 0.75rem;
  color: var(--text-secondary);
}

.update-content {
  padding-left: 12px;
}

.update-content p {
  color: var(--text-primary);
  line-height: 1.4;
  font-size: 0.85rem;
  margin: 0;
}

.no-log {
  color: var(--text-secondary);
  font-style: italic;
}

/* 无版本信息 */
.no-version {
  text-align: center;
  padding: 40px 20px;
  color: var(--text-secondary);
}

.no-version p {
  margin: 0;
  font-size: 0.9rem;
}

/* 更多按钮 */
.more-btn {
  background: transparent;
  color: var(--primary);
  border: 1px solid var(--primary);
  padding: 8px 16px;
  font-size: 0.8rem;
  border-radius: 4px;
  margin-top: 12px;
  cursor: pointer;
  transition: all 0.3s;
  width: 100%;
  font-weight: 500;
}

.more-btn:hover {
  background: var(--primary);
  color: var(--text-primary);
  box-shadow: 0 4px 12px rgba(0, 180, 255, 0.25);
}


.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: var(--text-primary);
}

.loading-spinner {
  border: 4px solid rgba(0, 180, 255, 0.1);
  border-radius: 50%;
  border-top: 4px solid var(--primary);
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  background: rgba(255, 0, 0, 0.1);
  color: #ff4444;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  text-align: center;
  border: 1px solid rgba(255, 0, 0, 0.2);
}

.retry-btn {
  background: var(--primary);
  color: var(--text-primary);
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  margin-top: 10px;
  cursor: pointer;
  transition: all 0.3s;
}

.retry-btn:hover {
  background: var(--secondary);
  box-shadow: 0 0 15px var(--primary);
}


/* 二维码弹窗样式 */
.qr-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
}

.qr-modal {
  background: var(--card-bg);
  border-radius: 16px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 0 30px rgba(0, 180, 255, 0.3);
  border: 1px solid rgba(0, 180, 255, 0.3);
  backdrop-filter: blur(15px);
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-50px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.qr-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(0, 180, 255, 0.2);
  background: linear-gradient(135deg, rgba(0, 180, 255, 0.1), rgba(255, 0, 255, 0.1));
}

.qr-modal-header h3 {
  color: var(--primary);
  margin: 0;
  font-size: 20px;
  text-shadow: 0 0 10px rgba(0, 180, 255, 0.5);
}

.close-btn {
  background: transparent;
  border: none;
  color: var(--text-secondary);
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s;
}

.close-btn:hover {
  background: rgba(255, 0, 0, 0.2);
  color: #ff4444;
  transform: rotate(90deg);
}

.qr-modal-content {
  padding: 24px;
  max-height: 60vh;
  overflow-y: auto;
}

.qr-code-container {
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
}

.qr-code-image {
  position: relative;
  width: 200px;
  height: 200px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 0 20px rgba(0, 180, 255, 0.2);
  border: 2px solid rgba(0, 180, 255, 0.3);
}

.download-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: rgba(0, 180, 255, 0.05);
  transition: transform 0.3s ease;
}

.download-image:hover {
  transform: scale(1.05);
}

.qr-code-placeholder {
  width: 200px;
  height: 200px;
  border: 2px dashed rgba(0, 180, 255, 0.3);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 180, 255, 0.05);
  color: var(--text-secondary);
}

.qr-icon {
  font-size: 48px;
  margin-bottom: 10px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

.download-tips {
  background: rgba(0, 180, 255, 0.05);
  border-radius: 8px;
  padding: 16px;
  border-left: 4px solid var(--primary);
}

.download-tips h4 {
  color: var(--primary);
  margin: 0 0 12px 0;
  font-size: 16px;
}

.download-tips ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.download-tips li {
  color: var(--text-secondary);
  margin-bottom: 8px;
  padding-left: 20px;
  position: relative;
  font-size: 14px;
}

.download-tips li::before {
  content: '•';
  color: var(--primary);
  position: absolute;
  left: 0;
  font-weight: bold;
}

.qr-modal-footer {
  display: flex;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid rgba(0, 180, 255, 0.2);
  background: rgba(0, 0, 0, 0.1);
}

.cancel-btn, .confirm-btn {
  flex: 1;
  padding: 12px 20px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.cancel-btn {
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid rgba(0, 180, 255, 0.3);
}

.cancel-btn:hover {
  background: rgba(255, 0, 0, 0.1);
  border-color: #ff4444;
  color: #ff4444;
}

.confirm-btn {
  background: var(--quantum-glow);
  color: var(--text-primary);
}

.confirm-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 0 20px var(--primary);
}

.more-btn {
  background: transparent;
  color: var(--primary);
  border: 1px solid var(--primary);
  padding: 8px 16px;
  font-size: 14px;
  border-radius: 6px;
  margin-top: 15px;
  cursor: pointer;
  transition: all 0.3s;
  width: 100%;
}

.more-btn:hover {
  background: var(--primary);
  color: var(--text-primary);
  box-shadow: 0 0 15px var(--primary);
}

/* 添加故障艺术效果 */
@keyframes glitch {
  0% {
    transform: translate(0);
  }
  20% {
    transform: translate(-2px, 2px);
  }
  40% {
    transform: translate(-2px, -2px);
  }
  60% {
    transform: translate(2px, 2px);
  }
  80% {
    transform: translate(2px, -2px);
  }
  100% {
    transform: translate(0);
  }
}

.glitch-effect {
  position: relative;
}

.glitch-effect::before,
.glitch-effect::after {
  content: attr(data-text);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.8;
}

.glitch-effect::before {
  color: var(--accent);
  animation: glitch 0.3s infinite;
  clip-path: polygon(0 0, 100% 0, 100% 45%, 0 45%);
  transform: translate(-2px, -2px);
}

.glitch-effect::after {
  color: var(--primary);
  animation: glitch 0.3s infinite reverse;
  clip-path: polygon(0 55%, 100% 55%, 100% 100%, 0 100%);
  transform: translate(2px, 2px);
}

.image-loading {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(5px);
}

.image-loading .loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(0, 180, 255, 0.1);
  border-radius: 50%;
  border-top: 3px solid var(--primary);
  animation: spin 1s linear infinite;
}

.image-loading p {
  color: var(--text-primary);
  margin-top: 10px;
  font-size: 12px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .latest-version-showcase {
    max-width: 100%;
    margin: 0;
  }
  
  .showcase-content {
    padding: 24px;
  }
  
  .software-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .software-icon {
    width: 48px;
    height: 48px;
  }
  
  .software-icon svg {
    width: 28px;
    height: 28px;
  }
  
  .software-name {
    font-size: 1.5rem;
  }
  
  .version-main-info {
    grid-template-columns: 1fr;
  }
  
  .version-number-display,
  .version-date-display {
    padding: 16px;
  }
  
  .version-number-large {
    font-size: 1.6rem;
  }
  
  .download-btn-enhanced {
    width: 100%;
    padding: 14px 32px;
    font-size: 1rem;
  }
  
  .expanded-cards {
    grid-template-columns: 1fr;
  }
  
  .more-versions-btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .showcase-content {
    padding: 20px;
  }
  
  .software-header {
    gap: 12px;
  }
  
  .software-icon {
    width: 40px;
    height: 40px;
  }
  
  .software-icon svg {
    width: 24px;
    height: 24px;
  }
  
  .software-name {
    font-size: 1.3rem;
  }
  
  .software-subtitle {
    font-size: 0.8rem;
  }
  
  .latest-badge {
    padding: 6px 12px;
  }
  
  .badge-text {
    font-size: 0.75rem;
  }
  
  .version-number-large {
    font-size: 1.4rem;
  }
  
  .date-text {
    font-size: 1rem;
  }
  
  .download-btn-enhanced {
    padding: 12px 24px;
    font-size: 0.95rem;
    min-width: 180px;
  }
  
  .description-content {
    font-size: 0.9rem;
  }
  
  .download-tips {
    font-size: 0.85rem;
  }
}
</style>
